import { useState, useEffect, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { Balloon } from './Balloon';
import { Score } from './Score';
import { Toast } from './Toast';
import { Title } from './Title';
import { InfoButton } from './InfoButton';
import { RoadmapButton } from './RoadmapButton';
import { WindButton } from './WindButton';
import { colors } from '@/data/concepts';
import { AIConcept } from '@/types';
import { fetchConcepts } from '@/services/conceptsService';

const GameContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  cursor: default;
`;

const RestartOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  cursor: default;
`;

const RestartMessage = styled.h2`
  color: white;
  font-size: 48px;
  margin-bottom: 30px;
  text-align: center;
`;

const RestartButton = styled.button`
  background: linear-gradient(135deg, #6B8E23 0%, #556B2F 100%);
  color: white;
  font-size: 24px;
  font-weight: bold;
  padding: 20px 50px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(107, 142, 35, 0.5);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(107, 142, 35, 0.7);
  }

  &:active {
    transform: translateY(-2px);
  }
`;

interface BalloonState {
  id: string;
  concept: AIConcept;
  x: number;
  y: number;
  color: string;
}

const MAX_BALLOONS = 15;

export const Game = () => {
  const [score, setScore] = useState(0);
  const [totalScore, setTotalScore] = useState(() => {
    const saved = localStorage.getItem('ai-balloon-total-score');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [balloons, setBalloons] = useState<BalloonState[]>([]);
  const [toastConcept, setToastConcept] = useState<AIConcept | null>(null);
  const [isToastVisible, setIsToastVisible] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);

  const createBalloon = useCallback((concept: AIConcept): BalloonState => {
    const color = colors[Math.floor(Math.random() * colors.length)];
    
    // Balloon size: 120px width x 140px height + 30px string
    const balloonWidth = 120;
    const balloonHeight = 170; // 140px balloon + 30px string
    const padding = 20; // Extra padding from edges
    
    const maxX = window.innerWidth - balloonWidth - padding;
    const maxY = window.innerHeight - balloonHeight - padding;
    
    const x = padding + Math.random() * Math.max(0, maxX - padding);
    const y = padding + Math.random() * Math.max(0, maxY - padding);
    
    return {
      id: `${Date.now()}-${Math.random()}`,
      concept,
      x,
      y,
      color,
    };
  }, []);

  const handlePop = useCallback((concept: AIConcept, balloonId: string) => {
    setScore(prev => prev + 1);
    setTotalScore(prev => {
      const newTotal = prev + 1;
      localStorage.setItem('ai-balloon-total-score', newTotal.toString());
      return newTotal;
    });
    setToastConcept(concept);
    setIsToastVisible(true);
    
    setBalloons(prev => {
      const newBalloons = prev.filter(b => b.id !== balloonId);
      // Check if this was the last balloon
      if (newBalloons.length === 0 && !gameCompleted) {
        setTimeout(() => setGameCompleted(true), 500);
      }
      return newBalloons;
    });
  }, [gameCompleted]);

  const closeToast = useCallback(() => {
    setIsToastVisible(false);
  }, []);

  const handleRestart = useCallback(async () => {
    setScore(0);
    setBalloons([]);
    setGameCompleted(false);
    setIsToastVisible(false);
    
    // Re-initialize balloons
    try {
      const fetchedConcepts = await fetchConcepts(MAX_BALLOONS);
      
      for (let i = 0; i < fetchedConcepts.length; i++) {
        setTimeout(() => {
          setBalloons(prev => [...prev, createBalloon(fetchedConcepts[i])]);
        }, i * 200);
      }
    } catch (error) {
      console.error('Failed to restart game:', error);
    }
  }, [createBalloon]);

  const handleWind = useCallback(() => {
    const padding = 20;
    const balloonWidth = 120;
    const balloonHeight = 170;

    setBalloons(prev => prev.map(balloon => {
      const force = 110 + Math.random() * 170;
      const angle = Math.random() * Math.PI * 2;

      const newX = Math.max(
        padding,
        Math.min(window.innerWidth - balloonWidth - padding, balloon.x + Math.cos(angle) * force)
      );
      const newY = Math.max(
        padding,
        Math.min(window.innerHeight - balloonHeight - padding, balloon.y + Math.sin(angle) * force)
      );

      return { ...balloon, x: newX, y: newY };
    }));
  }, []);

  // Initialize balloons - fetch concepts from API
  useEffect(() => {
    const initializeBalloons = async () => {
      try {
        // Fetch concepts from API (will fallback to local if API not available)
        const fetchedConcepts = await fetchConcepts(MAX_BALLOONS);
        
        // Store concepts in a ref or state if needed, or use them directly
        for (let i = 0; i < fetchedConcepts.length; i++) {
          setTimeout(() => {
            const concept = fetchedConcepts[i];
            const color = colors[Math.floor(Math.random() * colors.length)];
            
            const balloonWidth = 120;
            const balloonHeight = 170;
            const padding = 20;
            
            const maxX = window.innerWidth - balloonWidth - padding;
            const maxY = window.innerHeight - balloonHeight - padding;
            
            const x = padding + Math.random() * Math.max(0, maxX - padding);
            const y = padding + Math.random() * Math.max(0, maxY - padding);
            
            setBalloons(prev => [...prev, {
              id: `${Date.now()}-${Math.random()}`,
              concept,
              x,
              y,
              color,
            }]);
          }, i * 200);
        }
      } catch (error) {
        console.error('Failed to initialize balloons:', error);
      }
    };

    initializeBalloons();
  }, []);

  // Don't add new balloons automatically - game ends when all are popped

  return (
    <GameContainer>
      <Title />
      <Score score={score} totalScore={totalScore} />
      <InfoButton />
      <RoadmapButton />
      <WindButton onWind={handleWind} />
      <Toast 
        concept={toastConcept} 
        isVisible={isToastVisible} 
        onClose={closeToast} 
      />
      
      <AnimatePresence>
        {balloons.map((balloon) => (
          <Balloon
            key={balloon.id}
            concept={balloon.concept}
            x={balloon.x}
            y={balloon.y}
            color={balloon.color}
            onPop={(concept) => handlePop(concept, balloon.id)}
          />
        ))}
      </AnimatePresence>

      {gameCompleted && (
        <RestartOverlay>
          <RestartMessage>🎉 Parabéns! Você estourou todas as bolhas! 🎉</RestartMessage>
          <RestartButton onClick={handleRestart}>
            Jogar Novamente
          </RestartButton>
        </RestartOverlay>
      )}
    </GameContainer>
  );
};
